/**
 * 对话页面工具函数
 */
import type { IMessage, IMessageImage } from "@typing/wsui/getMessages";

/**
 * 创建 mock 用户消息
 * @param content 消息内容
 * @param images 图片列表
 * @returns IMessage 对象
 */
export const createMockUserMessage = (content: string, images?: IMessageImage[]): IMessage => ({
    message_id: `temp-user-${Date.now()}`,
    poster: {
        name: "我",
        avatar: "",
        role: "user",
    },
    sections: [
        {
            section_idx: "sec-0",
            segments: [
                {
                    segment_idx: "seg-0",
                    type: "text",
                    content,
                    is_finished: true,
                    tool_tip: {},
                },
            ],
            tool_tip: {},
        },
    ],
    images,
});

/**
 * 创建 mock Bot 消息（loading 占位）
 * @returns IMessage 对象（带 isLoading: true）
 */
export const createMockBotMessage = (): IMessage => ({
    message_id: `temp-bot-${Date.now()}`,
    poster: {
        name: "AI助手",
        avatar: "",
        role: "bot",
    },
    sections: [],
    isLoading: true,
});

/**
 * 参考文献数据结构
 */
export interface IReference {
    id: string;
    title: string;
    index: number;
    sub_title: string;
    source: string;
    url: string;
    tool_tip?: {
        tools?: Array<{
            type: "like" | "unlike";
            status: number;
        }>;
    };
}

/**
 * 参考文献响应结构
 */
interface IReferenceResponse {
    title?: string;
    list?: IReference[];
}

/**
 * 解析参考文献 JSON 字符串
 * @param content JSON 格式的参考文献数据
 * @returns 参考文献列表
 */
export const parseReference = (content: string): IReference[] => {
    try {
        const data: IReferenceResponse = JSON.parse(content);
        return data.list || [];
    } catch (error) {
        console.error("解析参考文献失败:", error);
        return [];
    }
};

/**
 * 图标状态类型
 */
export type IconState = "default" | "hover" | "selected";

/**
 * 获取工具图标
 * @param toolType 工具类型
 * @param state 图标状态：default-默认态, hover-悬停态, selected-选中态
 * @returns 图标路径
 */
export const getReferenceToolIcon = (toolType: string, state: IconState = "default"): string => {
    const iconMap: Record<string, Record<IconState, string>> = {
        like: {
            default: "https://muzhi-public-pic.cdn.bcebos.com/100115351326",
            hover: "https://muzhi-public-pic.cdn.bcebos.com/100115337404",
            selected: "https://muzhi-public-pic.cdn.bcebos.com/100115338618",
        },
        unlike: {
            default: "https://muzhi-public-pic.cdn.bcebos.com/100115351327",
            hover: "https://muzhi-public-pic.cdn.bcebos.com/100115337409",
            selected: "	https://muzhi-public-pic.cdn.bcebos.com/100115338616",
        },
        copy: {
            default: "https://muzhi-public-pic.cdn.bcebos.com/100115337410",
            hover: "https://muzhi-public-pic.cdn.bcebos.com/100115337411",
            selected: "https://muzhi-public-pic.cdn.bcebos.com/100115337410",
        },
    };
    return iconMap[toolType]?.[state] || iconMap[toolType]?.default || "";
};
/**
 * 获取工具图标
 * @param toolType 工具类型
 * @param state 图标状态：default-默认态, hover-悬停态, selected-选中态
 * @returns 图标路径
 */
export const getToolIcon = (toolType: string, state: IconState = "default"): string => {
    const iconMap: Record<string, Record<IconState, string>> = {
        like: {
            default: "https://muzhi-public-pic.cdn.bcebos.com/100115337402",
            hover: "https://muzhi-public-pic.cdn.bcebos.com/100115337404",
            selected: "https://muzhi-public-pic.cdn.bcebos.com/100115338618",
        },
        unlike: {
            default: "https://muzhi-public-pic.cdn.bcebos.com/100115337407",
            hover: "https://muzhi-public-pic.cdn.bcebos.com/100115337409",
            selected: "	https://muzhi-public-pic.cdn.bcebos.com/100115338616",
        },
        copy: {
            default: "https://muzhi-public-pic.cdn.bcebos.com/100115337410",
            hover: "https://muzhi-public-pic.cdn.bcebos.com/100115337411",
            selected: "https://muzhi-public-pic.cdn.bcebos.com/100115337410",
        },
    };
    return iconMap[toolType]?.[state] || iconMap[toolType]?.default || "";
};
